import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionTitle } from '@/components/common/SectionTitle';
import { Card, CardContent } from '@/components/ui/card';
import { studyMaterials, testSeries } from '@/data/studyMaterialData';
import { BookOpen, ClipboardList, ShoppingCart, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import SuccessBanner from '../assets/images/success/01.jpg';

const StudyMaterialPage = () => {
    return (
        <div>
            <PageHeader
                title="Study Material & Test Series"
                subtitle="Expert-designed material to boost your JEE / NEET preparation"
                breadcrumbs={[
                    { label: 'Home', path: '/' },
                    { label: 'Study Material' }
                ]}
            />

            {/* ================= STUDY MATERIAL ================= */}
            <section className="section-padding bg-surface-alt">
                <div className="container-custom">
                    <SectionTitle
                        title="Study Material"
                        description="Concept-driven modules prepared by expert faculty"
                        className="mb-12"
                    />

                    <div className="grid md:grid-cols-2 gap-8">
                        {studyMaterials.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className="border-2 hover:border-primary hover:shadow-xl transition">
                                    <CardContent className="p-6 space-y-3">
                                        <BookOpen className="h-8 w-8 text-primary mb-2" />

                                        <h3 className="text-lg font-bold">{item.title}</h3>
                                        <p><strong>Class:</strong> {item.class}</p>
                                        <p><strong>Includes:</strong> {item.includes}</p>
                                        <p><strong>Format:</strong> {item.format}</p>

                                        <div className="flex items-center justify-between mt-4">
                                            <span className="text-xl font-bold text-primary">
                                                {item.price}
                                            </span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= TEST SERIES ================= */}
            <section className="section-padding bg-background">
                <div className="container-custom">
                    <SectionTitle
                        title="Test Series"
                        description="Exam-like tests with detailed performance analysis"
                        className="mb-12"
                    />

                    <div className="grid md:grid-cols-2 gap-8">
                        {testSeries.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className="border-2 hover:border-primary hover:shadow-xl transition">
                                    <CardContent className="p-6 space-y-3">
                                        <ClipboardList className="h-8 w-8 text-secondary mb-2" />

                                        <h3 className="text-lg font-bold">{item.title}</h3>
                                        <p><strong>Tests:</strong> {item.tests}</p>
                                        <p><strong>Mode:</strong> {item.mode}</p>

                                        <div className="flex items-center justify-between mt-4">
                                            <span className="text-xl font-bold text-primary">
                                                {item.price}
                                            </span>
                                            <a href="/enquiry" className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary-dark transition">
                                                Register Now
                                            </a>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= CTA ================= */}
            <section className="relative min-h-[60vh] flex items-center bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url(${SuccessBanner})`,
                }} >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/40 to-secondary/40" />
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-accent-gold rounded-full blur-3xl animate-float" />
                    <div
                        className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-light rounded-full blur-3xl animate-float"
                        style={{ animationDelay: "1s" }}
                    />
                    <div
                        className="absolute top-1/2 left-1/2 w-80 h-80 bg-primary-light rounded-full blur-3xl animate-float"
                        style={{ animationDelay: "2s" }}
                    />
                </div>

                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="max-w-4xl mx-auto text-center text-primary-foreground"
                    >
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
                            Start Your Preparation with the Best Resources
                        </h2>
                        <p className="text-lg sm:text-xl text-primary-foreground/90 mb-10 leading-relaxed">
                            Trusted by hundreds of JEE & NEET achievers
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/contact">
                                <Button size="lg" className="bg-secondary hover:bg-secondary-dark text-secondary-foreground shadow-xl hover:shadow-2xl transition-all text-base px-8">
                                    Contact for Purchase
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default StudyMaterialPage;
